/**
 * PageContext — bounded contextual awareness for Ask SONMI.
 *
 * Each page can declare what SONMI "sees" while the user is on it. When
 * Ask SONMI opens, the last-set context is included in the request payload
 * so the assistant can answer visible-context questions without the user
 * restating what they're looking at.
 *
 * Deliberately minimal:
 *   • Not universal screen capture
 *   • Not continuous monitoring
 *   • Not pixel vision
 *   • Only what a page voluntarily publishes
 *
 * Usage on a page:
 *   const setPageContext = useSetPageContext();
 *   useEffect(() => { setPageContext(pageCtx); return () => setPageContext(null); }, [pageCtx]);
 */
import React, { createContext, useCallback, useContext, useRef, useState } from "react";

const PageContextCtx = createContext(null);

export function PageContextProvider({ children }) {
  // We keep a plain state for reactive consumers AND a ref so setters can
  // read latest without triggering re-renders.
  const [context, setContext] = useState(null);
  const ref = useRef(null);

  const set = useCallback((next) => {
    ref.current = next;
    setContext(next);
  }, []);

  const get = useCallback(() => ref.current, []);

  return (
    <PageContextCtx.Provider value={{ context, set, get }}>
      {children}
    </PageContextCtx.Provider>
  );
}

export function useSetPageContext() {
  const ctx = useContext(PageContextCtx);
  return ctx?.set || (() => {});
}

export function usePageContext() {
  const ctx = useContext(PageContextCtx);
  return ctx?.context ?? null;
}

export function useGetPageContext() {
  const ctx = useContext(PageContextCtx);
  return ctx?.get || (() => null);
}
